import { Typography, useToast } from "@hellocarbo/lasagne";
import { use, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  LoaderFunctionArgs,
  useActionData,
  useLoaderData,
  useSubmit,
} from "react-router";
import invariant from "tiny-invariant";

import { ReviewBanner } from "@/components/Banner";
import {
  Academy,
  CarbonCalculator,
  CommunicationKit,
  DownloadActionPlan,
  DownloadAttestation,
  DownloadFootprintAnalysis,
  Medal,
  Referral,
  SharingSettings,
} from "@/components/Communicate";
import PageWrapper from "@/components/ui/PageWrapper";
import { UserContext } from "@/contexts";
import {
  Review as TReview,
  TMedalData,
  TReviewCommunicateResponse,
  TReviewSharingStatus,
} from "@/types";
import { formatEndDate } from "@/utils/formatDate";

export async function loader({ params }: LoaderFunctionArgs) {
  const { reviewId } = params;

  invariant(reviewId, "No review id found!");

  if (reviewId === "free-trial") {
    return {
      review: null,
      hasOtherReviewsAtSameDate: false,
    };
  }

  // Fetch review communicate data (includes has_other_reviews_at_same_date)
  const reviewCommunicateResponse = await fetch(
    `/api/client/reviews/${reviewId}/communicate`
  );

  if (reviewCommunicateResponse.status !== 200) {
    throw data("Failed to load the review communication data", {
      status: reviewCommunicateResponse.status,
    });
  }

  const { data: reviewCommunicateData } =
    (await reviewCommunicateResponse.json()) as {
      data: TReviewCommunicateResponse;
    };

  // Fetch review data (period start and period end)
  const reviewResponse = await fetch(`/api/client/reviews/${reviewId}`);

  if (reviewResponse.status !== 200) {
    throw data("Failed to load the review data", {
      status: reviewResponse.status,
    });
  }

  const {
    data: { period_start: periodStart, period_end: periodEnd },
  } = (await reviewResponse.json()) as {
    data: TReview;
  };

  const hasOtherReviewsAtSameDate =
    reviewCommunicateData.has_other_reviews_at_same_date || false;

  // Fetch carbo medals
  const carboMedalsResponse = await fetch("/api/client/carbo_medals/");

  if (carboMedalsResponse.status !== 200) {
    throw data("Failed to load the medals data", {
      status: carboMedalsResponse.status,
    });
  }

  const { data: medalData } = (await carboMedalsResponse.json()) as {
    data: TMedalData;
  };

  return {
    medalData,
    reviewCommunicateData,
    periodStart,
    periodEnd,
    hasOtherReviewsAtSameDate,
  };
}

export async function action({ request, params }: ActionFunctionArgs) {
  const { reviewId } = params;
  invariant(reviewId, "No review id found!");

  const formData = await request.formData();
  const status = formData.get("status");

  const response = await fetch(
    `/api/client/reviews/${reviewId}/sharing_status`,
    {
      method: "PATCH",
      body: JSON.stringify({ sharing_status: status }),
      headers: { "Content-Type": "application/json" },
    }
  );

  if (response.status !== 200) {
    return { ok: false };
  }

  const { data: review } = (await response.json()) as {
    data: TReviewCommunicateResponse;
  };

  return { ok: true, newStatus: review.sharing_status, reviewId: review.id };
}

function Communicate() {
  const { t } = useTranslation("communicate");
  const {
    medalData,
    reviewCommunicateData,
    periodStart,
    periodEnd,
    hasOtherReviewsAtSameDate,
  } = useLoaderData<typeof loader>();
  const { user } = use(UserContext);
  const submit = useSubmit();
  const actionData = useActionData<typeof action>();
  const { toast } = useToast();

  useEffect(() => {
    if (actionData?.newStatus === "private") {
      toast({
        title: t("communicate:sharingSettings.publicFootprintDisabled"),
        intent: "positive",
      });
    }
  }, [actionData]);

  const isFreeTrial = user?.company.subscriptionType === "start";

  return (
    <PageWrapper>
      <div className="text-typography mx-xl my-lg">
        <Typography step="3" as="h1" weight="bold" className="mb-sm">
          {t("communicate:communication")}
        </Typography>
        <div className="gap-sm lg:gap-md grid grid-cols-1">
          {!reviewCommunicateData?.score.is_external && (
            <>
              <div className="col-span-full">
                <Typography step="2" as="h2" weight="bold">
                  {t("communicate:shareCarbonFootprint")}
                </Typography>
                {reviewCommunicateData && (
                  <Typography step="1" as="h3" weight="bold">
                    {t("communicate:sharingSettings.footprintFromTo", {
                      start: t("formatter:date", {
                        value: new Date(periodStart),
                      }),
                      end: t("formatter:date", {
                        value: formatEndDate(new Date(periodEnd)),
                      }),
                    })}
                  </Typography>
                )}
              </div>
              <div className="gap-sm lg:gap-y-md mb-sm grid grid-cols-2">
                <div className="col-span-2">
                  <SharingSettings
                    status={
                      reviewCommunicateData?.sharing_status ||
                      ("private" as TReviewSharingStatus)
                    }
                    url={reviewCommunicateData?.public_review_url}
                    updateSharingStatus={(newStatus) =>
                      submit({ status: newStatus }, { method: "patch" })
                    }
                    hideComparison={reviewCommunicateData?.hide_comparison}
                    hideEconomicIntensity={
                      reviewCommunicateData?.hide_economic_intensity
                    }
                    hideMarketingSpending={reviewCommunicateData?.hide_spending}
                  />
                </div>
                <DownloadAttestation
                  reviewId={reviewCommunicateData?.id}
                  companyHasSubsidiaries={
                    reviewCommunicateData?.company_with_subsidiaries
                  }
                  hasOtherReviewsAtSameDate={hasOtherReviewsAtSameDate}
                />
                {(reviewCommunicateData?.calculated_scopes.ghg_2024 ||
                  isFreeTrial) && (
                  <DownloadFootprintAnalysis
                    reviewId={reviewCommunicateData?.id}
                    companyHasSubsidiaries={
                      reviewCommunicateData?.company_with_subsidiaries
                    }
                    hasOtherReviewsAtSameDate={hasOtherReviewsAtSameDate}
                  />
                )}
              </div>
            </>
          )}
          <Typography step="2" as="h2" weight="bold">
            {t("communicate:communicateEngagement")}
          </Typography>
          <div className="gap-sm lg:gap-y-md mb-sm grid grid-cols-2">
            {medalData && <Medal medalData={medalData} />}

            {(reviewCommunicateData?.trajectories_url || isFreeTrial) && (
              <DownloadActionPlan
                url={reviewCommunicateData?.trajectories_url}
              />
            )}

            <CarbonCalculator />
          </div>
          <Typography step="2" as="h2" weight="bold">
            {t("communicate:ressources")}
          </Typography>
          <div className="gap-sm lg:gap-md grid grid-cols-3">
            <Academy />
            <CommunicationKit />
            {user?.company.subscriptionType !== "start" && <Referral />}
          </div>
        </div>
      </div>
      <ReviewBanner
        linkUrl={`/reviews/${reviewCommunicateData?.id}/feedback`}
      />
    </PageWrapper>
  );
}

export default Communicate;
